import { axiosRequest, updateHeaders } from '@/plugins/axios-common'
import { upperFirst } from 'lodash'

import { LEGAL_CASE_NAMESPACE } from './legal-case'
import { RECORD_NAMESPACE } from './record'

import { handleError } from '@/store/handle-error'
import { singularize } from '@/utilities'

export const actions = {
  /** RECORDS */
  getRecordPartiesModel: async ({ commit }, recordId) => {
    // Note: we'll always perform this request, rather than comparing
    // with current, as there is not an easy, definitive comparison to make.
    // (record/state.record.id could be out of sync with this recordId)
    return axiosRequest.
      get('/records/' + recordId + '/parties').
      then(response => {
        commit('setCurrentRecordPartiesModel', response.data)
      }).
      catch(handleError)
  },

  getAdverseIndividuals: async (_, recordId) => {
    return axiosRequest.
      get('/records/' + recordId + '/adverse').
      then(response => {
        return response.data
      }).
      catch(handleError)
  },

  /** DOCKET ENTRIES */
  createDocketEntryModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      post('/docket_entries', requestData).
      then(response => {
        const lastIndex = state.models.currentDocketEntries.length
        if (response.data.docket_entry) {
          commit('updateDocketEntriesModel', { index: lastIndex, data: response.data.docket_entry })

          return { newEntryId: response.data.docket_entry.id }
        } else {
          return response.data.map((docketEntry, index) => {
            commit('updateDocketEntriesModel', { index: lastIndex + index, data: docketEntry })

            return { newEntryId: docketEntry.id }
          })
        }
      }).
      catch(handleError)
  },

  openEntriesMassAssignmentDialog: async ({ commit }, value) => {
    commit('openEntriesMassAssignmentDialog', value)
  },

  updateDocketEntryModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch('/docket_entries/' + requestData.id, requestData.data).
      then(response => {
        let docketEntriesModel = {}
        if (response.data.docket_entry) {
          const index = state.models.currentDocketEntries.findIndex(d => d.id === response.data.docket_entry.id)
          docketEntriesModel = { index: index, data: response.data.docket_entry }
          commit('updateDocketEntriesModel', docketEntriesModel)
        }
        return docketEntriesModel
      }).catch(handleError)
  },

  deleteDocketEntryModel: async ({ commit, dispatch, state }, docketEntryId) => {
    return axiosRequest.
      delete('/docket_entries/' + docketEntryId).
      then(response => {
        if (response.data) {
          const index = state.models.currentDocketEntries.findIndex(d => d.id === docketEntryId)
          if (index < 0) return

          const docket = state.models.currentDocketEntries[index]
          const relations = (docket.child_docket_entry_relations || []).
            concat(docket.parent_docket_entry_relations || [])
          const deIdList = []

          relations.forEach(r => {
            if (!deIdList.includes(r.parent_docket_entry_id)) deIdList.push(r.parent_docket_entry_id)
            if (!deIdList.includes(r.child_docket_entry_id)) deIdList.push(r.child_docket_entry_id)

            commit('deleteDocketEntryRelation', r)
          })

          deIdList.forEach(deId => {
            const de = state.models.currentDocketEntries.find(d => d.id === deId)

            if (!de || de.child_docket_entry_relations.length || de.parent_docket_entry_relations.length) return

            dispatch(`${LEGAL_CASE_NAMESPACE}/removeDocketEntryInfoRelations`, parent.id, { root: true })
          })

          commit('deleteDocketEntryModel', index)
          dispatch(`${LEGAL_CASE_NAMESPACE}/deleteDocketEntryInfo`, docketEntryId, { root: true })
        }
      })
  },

  searchDocketEntriesInAllCasesModel: async (_, requestData) => {
    return axiosRequest.
      get(`/records/${requestData.id}/search_docket_entries`, {
        params: {
          search: requestData.search,
        },
      }).
      then(response => {
        return response.data
      }).
      catch(handleError)
  },

  /** USERS */
  getUsersModel: async ({ commit, state }) => {
    if (state.models.users.length > 0) return

    return axiosRequest.
      get('/users').
      then(response => commit('setUsersModel', response.data)).
      catch(handleError)
  },
  createUserModel: async ({ commit }, requestData) => {
    return axiosRequest.
      post('/users', {
        user: requestData,
      }).
      then(response => {
        if (response.data.user) {
          commit('addUserModel', {
            user: response.data.user,
          })
        }
      }).
      catch(handleError)
  },
  updateUserModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      put(`/users/${requestData.userId}`, {
        user: requestData.user,
      }).
      then(({ data }) => {
        if (data.user) commit('setUserModel', data.user)

        // if logged in user changed their password, a new access_token
        // will be returned, and needs to replace the one in headers
        if (data.access_token) {
          const token = data.access_token
          updateHeaders(token)
          commit('authSuccess', token)
        }
      }).
      catch(handleError)
  },
  getUserTypeOptionsModel: async ({ commit, state }) => {
    if (state.models.userTypeOptions.length === 0) {
      return axiosRequest.
        get('/options/users/user_type').
        then(response => {
          commit('setUserTypeOptionsModel', response.data.options)
          return response.data.options
        }).
        catch(handleError)
    }
  },
  unlockUser: async ({ commit }, requestData) => {
    return axiosRequest.
      post(`/users/${requestData.id}/unlock`).
      then(({ data }) => { if (data.user) commit('setUserModel', data.user) }).
      catch(handleError)
  },
  lockUser: async ({ commit, state }, requestData) => {
    return axiosRequest.
      post(`/users/${requestData.id}/lock`).
      then(({ data }) => { if (data.user) commit('setUserModel', data.user) }).
      catch(handleError)
  },
  checkUserTeamsAgainstRecordsTeams: async (_, data) => {
    return axiosRequest.
      get('/teams/check_user_teams_against_records_teams', { params: data }).
      then(response => response.data).
      catch(handleError)
  },

  /** AUTHENTICATION */
  refreshUserModel: async ({ commit }) => {
    return new Promise((resolve, reject) => {
      axiosRequest.
        get('/refresh_user').
        then(({ data }) => {
          if (data) {
            const token = data.access_token
            const user = data.user
            try {
              updateHeaders(token)
              commit('authSuccess', token)
              commit('setRefreshTokenExpiresAt', data.expires_at, { root: true })
              commit('setCurrentUser', user)
              resolve()
            } catch (e) {
              reject(e)
            }
          }
        }).
        catch(error => {
          // calling updateHeaders() without a token will delete current token
          updateHeaders()
          commit('authError')
          reject(error)
        })
    })
  },

  logoutUserModel: async ({ commit }) => {
    return new Promise(resolve => {
      commit('reset')
      axiosRequest.
        get('/logout').
        then(_ => {
          // calling updateHeaders() without a token will delete current token
          updateHeaders()
          resolve()
        }).
        catch(_ => {})
    })
  },

  /** SETTINGS */
  getOptions: async (_, requestData) => {
    // generic method for getting drop-down options
    return axiosRequest.
      get('/options/' + requestData.model + '/' + requestData.field).
      then(response => {
        return response.data
      }).
      catch(handleError)
  },
  getCountriesModel: async ({ commit, state }) => {
    if (state.models.countries.length === 0) {
      return axiosRequest.
        get('/countries').
        then(response => {
          commit('setCountriesModel', response.data)
        }).
        catch(handleError)
    }
  },
  getGendersModel: async ({ commit, state }) => {
    if (state.models.genders.length === 0) {
      return axiosRequest.
        get('/genders').
        then(response => {
          commit('setGendersModel', response.data)
        }).
        catch(handleError)
    }
  },

  /** SETTINGS, Editable */
  getMetadata: async ({ commit, state }, { requestData, force = false }) => {
    if (
      state.settings.metadata[requestData.jsType] !== undefined && (
        Object.keys(state.settings.metadata[requestData.jsType]).length === 0 ||
        force
      )
    ) {
      return axiosRequest.
        get(`/${requestData.rubyType}/meta`).
        then(response => {
          commit('setMetadata', { type: requestData.jsType, data: response.data })
        }).
        catch(handleError)
    }
  },

  getSettingsDataModel: async ({ commit, state }, { requestData }) => {
    if (!state.settings.retrieved[requestData.jsType]) {
      return axiosRequest.
        get(
          `/${requestData.rubyType}`,
          { params: { settings: true } }
        ).then(response => {
          commit('setSettingsDataModel', { type: requestData.jsType, data: response.data })
          // Update ModelDependents (FormFields)
          commit('updateModelDependentsFormFields', {
            type: requestData.jsType,
          })
        }).
        catch(handleError)
    }
  },

  getSettingsDataDetailsModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      get(`/${requestData.rubyType}/${requestData.id}`).
      then(response => {
        const singularRubyType = singularize(requestData.rubyType)
        if (response.data && response.data[singularRubyType]) {
          const entryTypeIndex =
          state.models[requestData.jsType].findIndex(l => l.id === requestData.id)
          commit('updateSettingsDataModel', {
            type: requestData.jsType,
            index: entryTypeIndex,
            data: response.data[singularRubyType],
          })
        }
      }).
      catch(handleError)
  },

  createSettingsDataModel: async ({ commit }, requestData) => {
    return axiosRequest.
      post(`/${requestData.rubyType}`,
        requestData.jsType === 'documentSets' ? requestData.data : requestData).
      then(response => {
        const singularRubyType = singularize(requestData.rubyType)
        if (response.data && response.data[singularRubyType]) {
          commit('setSettingsDataModel', {
            type: requestData.jsType,
            data: response.data[singularRubyType],
          })
          // Update ModelDependents (FormFields)
          commit('updateModelDependentsFormFields', {
            type: requestData.jsType,
          })
          return response.data[singularRubyType].id
        }
      }).
      catch(handleError)
  },
  updateSettingsDataModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch(`/${requestData.rubyType}/${requestData.id}`,
        requestData.jsType === 'documentSets' ? requestData.data : requestData).
      then(response => {
        const singularRubyType = singularize(requestData.rubyType)
        const entryTypeIndex =
          state.models[requestData.jsType].findIndex(d => d.id === requestData.id)
        if (response.data && response.data[singularRubyType]) {
          commit('updateSettingsDataModel', {
            index: entryTypeIndex,
            type: requestData.jsType,
            data: response.data[singularRubyType],
          })
        }
      })
  },
  deleteSettingsDataModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      delete(`/${requestData.rubyType}/${requestData.id}`).
      then(() => {
        const index = state.models[requestData.jsType].findIndex(e => e.id === requestData.id)
        if (index >= 0) {
          commit('deleteSettingsDataModel', { index: index, type: requestData.jsType })
          // Update ModelDependents (FormFields)
          commit('updateModelDependentsFormFields', {
            type: requestData.jsType,
          })
        }
      }).
      catch(handleError)
  },
  /** Settings data, Fetched outside of Settings view */
  getPreferenceCategoriesModel: async ({ commit, state }) => {
    if (state.models.preferenceCategories.length === 0) {
      return axiosRequest.
        get('/preference_categories').
        then(response => {
          commit('setPreferenceCategoriesModel', response.data)
        }).
        catch(handleError)
    }
  },
  getDocketEntryTypesModel: async ({ commit }) => {
    return axiosRequest.
      get('/docket_entry_types').
      then(response => {
        commit('setDocketEntryTypesModel', response.data)
      }).
      catch(handleError)
  },
  getFilteredDocketEntryTypesModel: async ({ commit }, requestData) => {
    return axiosRequest.
      get('/docket_entry_types', { params: requestData }).
      then(response => {
        commit('setFilteredDocketEntryTypesModel', response.data)
      }).
      catch(handleError)
  },
  getDocumentSetsModel: async ({ commit, state }) => {
    if (state.models.documentSets.length === 0) {
      return axiosRequest.
        get('/document_sets').
        then(response => {
          commit('setDocumentSetsModel', response.data)
        }).
        catch(handleError)
    }
  },
  getDocumentSetDetailsModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      get(`/document_sets/${requestData.id}`).
      then(response => {
        const index = state.models.documentSets.findIndex(d => d.id === requestData.id)
        commit('updateDocumentSetsModel', { index: index, data: response.data.document_set })
      }).
      catch(handleError)
  },
  getLegalCaseTypesModel: async ({ commit, state }) => {
    if (state.models.legalCaseTypes.length === 0) {
      return axiosRequest.
        get('/legal_case_types').
        then(response => {
          commit('setLegalCaseTypesModel', response.data)
        }).
        catch(handleError)
    }
  },

  getLanguagesModel: async ({ commit, state }) => {
    if (state.models.languages.length === 0) {
      return axiosRequest.
        get('/languages').
        then(response => {
          commit('setLanguagesModel', response.data)
        }).
        catch(handleError)
    }
  },
  getLocationsModel: async ({ commit, state }) => {
    if (state.models.locations.length === 0) {
      return axiosRequest.
        get('/locations').
        then(response => {
          commit('setLocationsModel', response.data)
        }).
        catch(handleError)
    }
  },
  getTeamsModel: async ({ commit, state }) => {
    if (state.models.teams.length === 0) {
      return axiosRequest.
        get('/teams').
        then(response => {
          commit('setTeamsModel', response.data)
        }).
        catch(handleError)
    }
  },
  getRolesModel: async ({ commit, state }) => {
    if (state.models.partyRoles.length === 0) {
      return axiosRequest.
        get('/roles').
        then(response => {
          commit('setPartyRolesModel', response.data)
        }).
        catch(handleError)
    }
  },

  /** FILES */
  uploadFilesDocketEntryModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch(`/docket_entries/${requestData.id}/upload`, requestData.fileToUpload).
      then(response => {
        const index = state.models.currentDocketEntries.findIndex(d => d.id === response.data.docket_entry.id)
        commit('updateDocketEntriesModel', { index: index, data: response.data.docket_entry })
      }).catch(handleError)
  },
  getAttachmentBlobModel: async (_, requestData) => {
    return axiosRequest.
      get(`/active_storage/attachments/${requestData.fileId}/download_blob`, {
        responseType: 'blob',
        onDownloadProgress: requestData.onDownloadProgress,
      }).
      then(response => {
        return response.data
      }).
      catch(handleError)
  },
  getThumbnailForAttachment: async (_, requestData) => {
    return axiosRequest.
      get(`/active_storage/attachments/${requestData.fileId}/send_representation`, {
        responseType: 'blob',
      }).then(response => {
        return response.data
      }).
      catch(handleError)
  },
  updateAttachmentFileName: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch(`/active_storage/attachments/${requestData.fileId}/update_filename`, {
        filename: requestData.fileName,
      }).then(response => {
        // TODO: request full file is returned, to update in mutations
        // as a single field of a nested prop in an obj array cannot be updated
        const dataIndex = requestData.forDocketEntry
          ? state.models.currentDocketEntries.findIndex(e => e.id === requestData.dataId)
          : state.models.documentSets.findIndex(d => d.id === requestData.dataId)
        let fileIndex = -1
        if (dataIndex >= 0) {
          fileIndex = requestData.forDocketEntry
            ? state.models.currentDocketEntries[dataIndex].files.findIndex(f => f.id === requestData.fileId)
            : state.models.documentSets[dataIndex].files.findIndex(f => f.id === requestData.fileId)
        }

        if (fileIndex >= 0) {
          commit('updateFileNameInDocketEntry', {
            index: dataIndex,
            fileIndex: fileIndex,
            data: response.data.attachment,
            forDocketEntry: requestData.forDocketEntry,
          })
        }
      }).
      catch(handleError)
  },
  destroyAttachmentModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      delete(`/active_storage/attachments/${requestData.fileId}`).
      then(() => {
        let entryIndex = -1; let fileIndex = -1
        if (requestData.forDataType === 'documentSets') {
          entryIndex = state.models.documentSets.findIndex(e => e.id === requestData.dataId)
          if (entryIndex >= 0) fileIndex = state.models.documentSets[entryIndex].files.findIndex(f => f.id === requestData.fileId)
        } else if (requestData.forDataType === 'docketEntries') {
          entryIndex = state.models.currentDocketEntries.findIndex(e => e.id === requestData.dataId)
          if (entryIndex >= 0) fileIndex = state.models.currentDocketEntries[entryIndex].files.findIndex(f => f.id === requestData.fileId)
        }
        if (entryIndex >= 0 && fileIndex >= 0) {
          commit('deleteAttachmentModel', {
            index: entryIndex,
            fileIndex: fileIndex,
            forDocketEntry: requestData.forDataType === 'docketEntries',
          })
        }
      }).
      catch(handleError)
  },

  /** WORKFLOWS */
  createWorkflowModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      post('/workflows', {
        workflow: requestData.workflow,
      }).
      then(response => {
        if (response.data) {
          const index = state.models[requestData.dataType].findIndex(d => d.id === response.data.workflow.workflowable_id)
          commit(`set${upperFirst(requestData.dataType)}WorkflowModel`,
            { index: index, data: response.data.workflow })
        }
      }).catch(handleError)
  },
  updateWorkflowModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch('/workflows/' + requestData.workflowId, {
        workflow: requestData.workflow,
      }).then(response => {
        if (response.data) {
          let wIndex = -1
          const index = state.models[requestData.dataType].findIndex(d => d.id === requestData.workflow.workflowable_id)
          if (index !== -1) wIndex = state.models[requestData.dataType][index].workflows.findIndex(w => w.id === response.data.workflow.id)

          commit(`update${upperFirst(requestData.dataType)}WorkflowModel`,
            { index: index, workflowIndex: wIndex, data: response.data.workflow })
        }
      }).catch(handleError)
  },
  deleteWorkflowModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      delete('/workflows/' + requestData.workflow.id, {
        data: { workflow: requestData.workflow },
      }).then(() => {
        const index = state.models[requestData.dataType].findIndex(w => w.id === requestData.workflow.workflowable_id)
        if (index !== -1) {
          const wIndex = state.models[requestData.dataType][index].workflows.findIndex(w => w.id === requestData.workflow.id)
          commit(`delete${upperFirst(requestData.dataType)}WorkflowModel`,
            { index: index, workflowIndex: wIndex })
        }
      }).catch(handleError)
  },

  /** INVOICED */
  getInvoicedCustomerBalance: async (_, requestData) => {
    return axiosRequest.
      get('/customer_balance', {
        params: {
          record_id: requestData.id,
        },
      }).
      then(response => {
        return response.data
      })
  },
  getInvoicedCustomerBillingHistory: async (_, requestData) => {
    return axiosRequest.
      get('/customer_billing_history', {
        params: {
          record_id: requestData.id,
        },
      }).
      then(response => {
        return response.data
      })
  },
  createInvoice: async ({ commit, state }, requestData) => {
    return axiosRequest.
      post('/create_invoice', {
        legal_case_id: requestData.legalCaseId,
      }).
      then(response => {
        // update corresponding case with invoice id
        const index = state[LEGAL_CASE_NAMESPACE].legalCases.findIndex(c => c.id === requestData.legalCaseId)
        const legalCase = index !== -1 ? state[LEGAL_CASE_NAMESPACE].legalCases[index] : null

        if (legalCase && index !== -1) {
          legalCase.invoiced_invoice_id = response.data.invoice_id
          commit(`${LEGAL_CASE_NAMESPACE}/setLegalCase`, legalCase, { root: true })
        }

        // make sure record this case belongs to is currentRecord
        // may not be necessary, but just in case

        if (state[RECORD_NAMESPACE].record.id === requestData.recordId &&
            !state[RECORD_NAMESPACE].record.invoiced_customer_id
        ) {
          commit(
            `${RECORD_NAMESPACE}/updateRecord`,
            { field: 'invoiced_customer_id', data: response.data.customer_id },
            { root: true }
          )
        }
      })
  },

  /** SITE SETTINGS */
  createSiteSetting: async ({ commit }, requestData) => {
    return axiosRequest.
      post('/site_settings', {
        site_setting: requestData,
      }).
      then(response => {
        commit('setSiteSettings', { fromSettings: true, data: response.data.site_setting })
      }).
      catch(handleError)
  },
  updateSiteSetting: async ({ commit, state }, requestData) => {
    return axiosRequest.
      patch(`/site_settings/${requestData.id}`, {
        site_setting: requestData,
      }).
      then(response => {
        const id = response.data.site_setting.id
        if (id) {
          const index = state.siteSettings.findIndex(s => s.id === id)
          commit('updateSiteSetting', { index: index, data: response.data.site_setting })
        }
      }).
      catch(handleError)
  },
  deleteSiteSetting: async ({ commit, state }, requestData) => {
    return axiosRequest.
      delete('/site_settings/' + requestData.id).
      then(_ => {
        const settingIndex = state.siteSettings.findIndex(s => s.id === requestData.id)
        commit('deleteSiteSetting', { index: settingIndex })
      }).
      catch(handleError)
  },
  getSiteSettings: async ({ commit, state }, requestData) => {
    // check if already loaded based on whether this call is from
    // admin Settings view or not
    if ((requestData.fromSettings && !state.settings.retrieved.siteSettings) ||
      !requestData.fromSettings) {
      return axiosRequest.
        get('/site_settings', {
          params: { settings: !!requestData.fromSettings },
        }).
        then(response => {
          commit('setSiteSettings', {
            data: response.data ? response.data : response,
            fromSettings: requestData.fromSettings,
          })
        }).
        catch(handleError)
    }
  },

  /**
   * OUTLOOK
   */
  authorizeOutlookCalendar: async ({ commit }) => {
    return axiosRequest.
      put('/users/outlook/authorize').
      then(response => commit('setCurrentUser', response.data)).
      catch(handleError)
  },
  deauthorizeOutlookCalendar: async ({ commit }) => {
    return axiosRequest.
      put('/users/outlook/deauthorize').
      then(response => commit('setCurrentUser', response.data)).
      catch(handleError)
  },

  //* BACKUP DOWNLOAD **/
  downloadBackupFile: async _ => {
    return axiosRequest({
      url: '/db_backup',
      method: 'get',
    }).
      then(response => response).
      catch(handleError)
  },

  /** AUTOMATED TEXT TEMPLATE */
  saveAutomatedTextTemplateModel: async ({ commit, state }, requestData) => {
    return axiosRequest.
      put('/automated_text_templates', requestData).
      then(response => {
        const docketEntryTypeId = response.data.automated_text_template.docket_entry_type_id
        if (docketEntryTypeId) {
          const index = state.models.docketEntryTypes.findIndex(d => d.id === docketEntryTypeId)
          commit('setDocketEntryTypesAutomatedTextTemplateModel', { index: index, data: response.data })
        }
      }).
      catch(handleError)
  },
}
